import { timezone } from '@DEPRECATED/utils';
import ptBR from '@DEPRECATED/utils/chart/locale/pt-br.json';
import { addDays, isValid } from 'date-fns';
import { zonedTimeToUtc } from 'date-fns-tz';
import merge from 'lodash/merge';

export function getOptions(properties) {
  const defaultYaxis = {
    tickAmount: 8,
    crosshairs: {
      show: false,
    },
    forceNiceScale: true,
    labels: {
      style: {
        colors: '#AAA',
        fontWeight: 500,
        fontFamily: 'Roboto',
        fontSize: '14px',
      },
      formatter: (value) => Number(value).toFixed(0),
    },
    tooltip: {
      enabled: false,
    },
  };

  const options = merge(
    {
      chart: {
        defaultLocale: 'pt-br',
        locales: [ptBR],
        toolbar: {
          show: false,
        },
        zoom: {
          enabled: false,
        },
        animations: {
          enabled: true,
          easing: 'easeinout',
          speed: 500,
          animateGradually: {
            enabled: true,
            delay: 250,
          },
          dynamicAnimation: {
            enabled: true,
            speed: 650,
          },
        },
      },
      grid: {
        borderColor: '#8c8c8c',
      },
      fill: {
        opacity: 1,
      },
      colors: ['#2558F8', '#EA7117', '#CCC', '#48E66D'],
      dataLabels: {
        enabled: true,
        enabledOnSeries: [0, 1, 2],
        offsetY: -10,
        background: {
          enabled: false,
          dropShadow: {
            enabled: false,
          },
        },
        style: {
          fontSize: '12px',
          fontFamily: 'Roboto, Arial, sans-serif',
          fontWeight: 500,
          colors: ['#AAA', '#AAA', '#CCC'],
        },
        formatter: (value) => Number(value).toFixed(0),
      },
      markers: {
        size: [0, 0, 4, 0],
        colors: '#CCC',
        strokeColors: '#CCC',
        strokeWidth: 2,
        fillOpacity: 0,
        hover: {
          size: 4,
          sizeOffset: 4,
        },
      },
      stroke: {
        width: [0, 0, 2, 2],
        curve: 'straight',
        dashArray: [0, 0, 0, 5],
      },
      xaxis: {
        type: 'datetime',
        max: new Date(2020, 11, 9).getTime(),
        crosshairs: {
          show: true,
          position: 'front',
        },
        labels: {
          style: {
            colors: '#AAA',
            fontWeight: 500,
            fontFamily: 'Roboto',
            fontSize: '14px',
          },
          format: 'MMMyy',
        },
        axisBorder: {
          show: false,
        },
        axisTicks: {
          show: true,
          borderType: 'solid',
          color: '#232D44',
          height: 5,
          offsetX: 0,
          offsetY: 0,
        },
        tickPlacement: 'between',
      },
      yaxis: [
        {
          ...defaultYaxis,
          seriesName: 'Compra',
          title: {
            text: 'COMPRA / VENDA',
            style: {
              color: '#a8adba',
            },
          },
        },
        {
          ...defaultYaxis,
          seriesName: 'Compra',
          show: false,
        },
        {
          ...defaultYaxis,
          seriesName: 'Saldo',
          opposite: true,
          title: {
            text: 'SALDO / LASTRO',
            style: {
              color: '#AAA',
            },
          },
        },
        {
          ...defaultYaxis,
          seriesName: 'Saldo',
          show: false,
        },
      ],
      legend: {
        show: true,
        fontWeight: 500,
        fontFamily: 'Roboto',
        fontSize: '12px',
        labels: {
          colors: '#AAA',
        },
        markers: {
          width: 15,
          height: 2,
          radius: 0,
        },
        position: 'top',
        itemMargin: {
          horizontal: 10,
          vertical: 0,
        },
        formatter(seriesName) {
          return [`${seriesName.toUpperCase()}`];
        },
        onItemClick: {
          toggleDataSeries: false,
        },
        onItemHover: {
          highlightDataSeries: false,
        },
      },
      tooltip: {
        enabled: true,
        theme: 'light',
        x: {
          show: true,
          format: 'MMMyy',
        },
      },
      plotOptions: {
        bar: {
          columnWidth: '90%',
        },
        dataLabels: {
          position: 'top',
        },
      },
    },
    properties,
  );

  if (properties.yaxis) {
    options.yaxis = properties.yaxis;
  }

  return options;
}

export function getCategories(data) {
  function parseDate(object) {
    return zonedTimeToUtc(object.period, timezone).toString();
  }

  if (!data) return [];

  if (!data.months) {
    return [parseDate(data)];
  }

  return data.months.map(parseDate);
}

export function getAmountSeries(data) {
  const options = [
    { name: 'Compra', type: 'bar', data: [] },
    { name: 'Venda', type: 'bar', data: [] },
    { name: 'Saldo', type: 'line', data: [] },
    { name: 'Lastro', type: 'line', data: [] },
  ];

  if (!data) return options;

  if (data.months) {
    data.months.forEach((month) => {
      options[0].data.push(month.purchase.quantityMwm);
      options[1].data.push(month.sale.quantityMwm);
      options[2].data.push(month.balanceMwm);
      options[3].data.push(month.energyBallast.balance);
    });
  } else {
    options[0].data.push(data.purchase.quantityMwm);
    options[1].data.push(data.sale.quantityMwm);
    options[2].data.push(data.balanceMwm);
    options[3].data.push(data.energyBallast.balance);
  }

  return options;
}

export function getSeriesAveragePrice(data) {
  const options = [
    { name: 'Compra', type: 'line', data: [] },
    { name: 'Venda', type: 'line', data: [] },
    { name: 'N', type: 'line', data: [] },
    { name: 'NE', type: 'line', data: [] },
    { name: 'SE', type: 'line', data: [] },
    { name: 'S', type: 'line', data: [] },
  ];

  if (!data) return options;

  if (data.months) {
    data.months.forEach((month) => {
      options[0].data.push(month.purchase.averagePrice);
      options[1].data.push(month.sale.averagePrice);
      options[2].data.push(month.submarket.n.pld);
      options[3].data.push(month.submarket.ne.pld);
      options[4].data.push(month.submarket.se.pld);
      options[5].data.push(month.submarket.s.pld);
    });
  } else {
    options[0].data.push(data.purchase.averagePrice);
    options[1].data.push(data.sale.averagePrice);
    options[2].data.push(data.submarket.n.pld);
    options[3].data.push(data.submarket.ne.pld);
    options[4].data.push(data.submarket.se.pld);
    options[5].data.push(data.submarket.s.pld);
  }

  return options;
}

export function getSeries(data, isAveragePrice) {
  const getValues = isAveragePrice ? getSeriesAveragePrice : getAmountSeries;

  return getValues(data);
}

export function getAnnotations(date) {
  const parsedDate = new Date(date);

  if (!isValid(parsedDate)) return [];

  const currentDate = zonedTimeToUtc(parsedDate, timezone);

  return [
    {
      x: addDays(currentDate, -15).getTime(),
      x2: addDays(currentDate, 15).getTime(),
      fillColor: 'rgba(255,255,255,0)',
      borderColor: 'cyan',
      strokeDashArray: 3,
      opacity: 1,
      label: {
        borderWidth: 0,
      },
    },
  ];
}
